import { QueryClient,queryOptions } from '@tanstack/vue-query';
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { storeToRefs } from 'pinia';

import { mixPanelEvents } from '@/common/constants';
import { type ListItem, useKeywordListStore } from '@/common/stores/lists';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { track } from '@/helpers/analytics/mixpanel';
import { axios } from '@/helpers/axios';
import type { KeywordListNameResource, KeywordListTermResource } from '@/types/laravel';


export interface KeywordListCreateFormData {
  name: string
}

const keywordListNamesKey = ['keyword-list', 'names'];
const keywordListTermsKey = ['keyword-list', 'terms'];

export function useKeywordListNamesQueryOptions() {
  return queryOptions({
    queryKey: keywordListNamesKey,
    queryFn: ({ signal }) => axios.get<KeywordListNameResource[]>('keywordlist/names', {
      signal,
    }),
    select: (data) => data.data,
  });
}

export function useKeywordListTermsQueryOptions() {
  return queryOptions({
    queryKey: keywordListTermsKey,
    queryFn: ({ signal }) => axios.get<KeywordListTermResource[]>('keywordlist/terms', {
      signal,
    }),
    select: (data) => data.data,
  });
}

export function useAddKeywordToListMutation(queryClient: QueryClient) {
  const toast = useTrackedToast();

  return useMutation({
    mutationFn: ({ keyword, listId }: { keyword: string, listId: number }) =>
      axios.post<KeywordListTermResource[]>('/keywordlist/save-keyword', {
        keyword,
        listId,
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(keywordListTermsKey, (old: { data: KeywordListTermResource[] }) => ({
        ...old,
        data: [...old.data, ...result['data']],
      }));
    },
    onSettled: (data, error) => {
      if (error) {
        toast.add({
          severity: 'warn',
          summary: 'Unable to save the keyword',
          detail: error.response?.data && typeof error.response.data === 'object' && 'error' in error.response.data
            ? (error.response.data as { error?: string }).error
            : 'An error occurred while saving the keyword.',
          life: 3000,
        });
      }
      queryClient.invalidateQueries({ queryKey: keywordListTermsKey });
    },
  });
}

export function useRemoveKeywordFromListsMutation(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async ({ keyword }: { keyword: string }) => {
      // Snapshot previous state
      const previous = queryClient.getQueryData<{ data: KeywordListTermResource[] }>(keywordListTermsKey);
      const ids = (previous?.data ?? []).filter((item) => item.keyword === keyword).map((item) => item.id);

      // Optimistic update
      queryClient.setQueryData(keywordListTermsKey, (old?: { data: KeywordListTermResource[] }) =>
        old ? { ...old, data: old.data.filter((item) => item.keyword !== keyword) } : old,
      );

      try {
        await axios.post('/keywordlist/delete-kl-term', { ids: ids });
      } catch (error) {
        // Rollback on error
        if (previous) {
          queryClient.setQueryData(keywordListTermsKey, previous);
        }
        throw error;
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keywordListTermsKey });
    },
  });
}

export function useRemoveKeywordFromListMutation(queryClient: QueryClient) {
  return useMutation({
    mutationFn: ({ id }: { id: number }) => axios.post('/keywordlist/delete-kl-term', { ids: [id] }),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(keywordListTermsKey, (old: { data: KeywordListTermResource[] }) => ({
        ...old,
        data: old.data.filter((item) => item.id !== id),
      }));
    },
  });
}

export function useRemoveListMutation(queryClient: QueryClient) {
  return useMutation({
    mutationFn: ({ id }: { id: number }) => axios.post('/keywordlist/delete-kl', { id }),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(keywordListTermsKey, (old: { data: KeywordListTermResource[] }) => ({
        ...old,
        data: old.data.filter((item) => item.listId !== id),
      }));
    },
  });
}

export function useKeywordListCreateMutation() {
  const { lists } = storeToRefs(useKeywordListStore());
  const toast = useTrackedToast();

  return useMutation({
    mutationFn: (data: KeywordListCreateFormData) => axios.post<ListItem>('keywordlist/save-name', data),
    onError: (error: AxiosError) => {
      toast.add({
        // @ts-expect-error - wrong types.
        detail: error.response?.data.error || 'Failed to create a new list. Please try again later.',
        life: 3000,
        severity: 'error',
        summary: 'Error',
        errorCode: error.response?.status ? `${error.response.status} LIST_CREATE_FAILED` : 'LIST_CREATE_FAILED',
      });
    },
    onSuccess: (response: AxiosResponse<ListItem>) => {
      lists.value.unshift(response.data);

      track(mixPanelEvents.CREATED_KEYWORD_LIST);

      toast.add({
        detail: 'List has been added successfully.',
        life: 3000,
        severity: 'success',
        summary: 'Success',
      });
    },
  });
}